import type { FeatureConfig } from '@skynet/feature-config';
import { UserOutlined } from '@ant-design/icons';
import { USER_DETAIL_ROUTE_PATH, USERS_LIST_URL, USERS_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Users list and user detail pages. The dashboard shell reads
 * this single source of truth instead of hardcoding the route, navigation
 * entry, header label and Cerbos action across its routing, navigation,
 * header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to `@skynet/sky-user`'s
 * components. The config imports nothing from `@skynet/sky-user`, so reading it
 * never drags the Users UI onto the boot path.
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:users`), and renaming any
 * of them hides the nav item until the policy is renamed too.
 */
export const usersConfig = {
  domain: 'Admin',
  section: 'users',
  route: {
    path: USERS_ROUTE_PATH,
    load: async () => ({ default: (await import('./Users')).Users }),
  },
  detailRoute: {
    path: USER_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./UserPage')).UserPage }),
  },
  nav: {
    key: 'admin.users',
    label: 'Users',
    icon: <UserOutlined />,
    url: USERS_LIST_URL,
  },
  header: { label: 'USERS' },
  cerbosAction: 'read:admin:users',
  skipWsLoginPaths: [USERS_LIST_URL],
} as const satisfies FeatureConfig;
